import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Truck, Hash, Users, Accessibility, BadgeCheck, BadgeX, CalendarDays, Clock } from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("de-DE") : "—");
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function VehicleDetailModal({ vehicle, open, onOpenChange }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!vehicle || !open) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/trips", { params: { vehicle_id: vehicle.id } });
        if (!cancelled) setTrips(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setTrips([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [vehicle, open]);

  const today = todayKey();
  const upcoming = useMemo(() => {
    return [...trips]
      .filter((t) => t.trip_date >= today)
      .sort((a, b) => (a.trip_date === b.trip_date ? (a.start_time || "").localeCompare(b.start_time || "") : a.trip_date.localeCompare(b.trip_date)))
      .slice(0, 8);
  }, [trips, today]);

  const totalCount = trips.length;
  const todayCount = trips.filter((t) => t.trip_date === today).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="vehicle-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            {vehicle?.license_plate || "Fahrzeug"}
            {vehicle && (
              vehicle.active
                ? <span className="inline-flex items-center gap-1 ml-2 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-200"><BadgeCheck className="h-3 w-3" /> Aktiv</span>
                : <span className="inline-flex items-center gap-1 ml-2 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200"><BadgeX className="h-3 w-3" /> Inaktiv</span>
            )}
          </DialogTitle>
          <DialogDescription>Details und nächste Einsätze des Fahrzeugs.</DialogDescription>
        </DialogHeader>

        {!vehicle ? (
          <p className="text-sm text-muted-foreground italic">Kein Fahrzeug ausgewählt.</p>
        ) : (
          <div className="space-y-4">
            {/* Stammdaten */}
            <Card className="p-4">
              <h3 className="text-sm font-semibold heading mb-2">Stammdaten</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <div className="flex items-center gap-2">
                  <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                  <dt className="text-muted-foreground">Kennzeichen:</dt>
                  <dd className="font-mono font-semibold" data-testid="vehicle-detail-plate">{vehicle.license_plate || "—"}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                  <dt className="text-muted-foreground">Typ:</dt>
                  <dd>{vehicle.vehicle_type || "—"}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <dt className="text-muted-foreground">Sitzplätze:</dt>
                  <dd>{vehicle.seat_capacity ?? "—"}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <Accessibility className="h-3.5 w-3.5 text-muted-foreground" />
                  <dt className="text-muted-foreground">Rollstuhlplätze:</dt>
                  <dd>{vehicle.wheelchair_capacity ?? "—"}</dd>
                </div>
              </dl>
              {vehicle.notes && (
                <p className="mt-3 text-sm text-muted-foreground border-l-2 border-blue-400 pl-3 italic">
                  {vehicle.notes}
                </p>
              )}
            </Card>

            {/* Auslastung */}
            <Card className="p-4">
              <h3 className="text-sm font-semibold heading mb-2 flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-blue-600" /> Einsätze
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div className="rounded-md border border-border bg-card px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Heute</div>
                  <div className="text-lg font-semibold tabular-nums" data-testid="vehicle-detail-today-count">{todayCount}</div>
                </div>
                <div className="rounded-md border border-border bg-card px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Gesamt protokolliert</div>
                  <div className="text-lg font-semibold tabular-nums" data-testid="vehicle-detail-total-count">{totalCount}</div>
                </div>
              </div>

              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Nächste 8 Einsätze</h4>
              {loading ? (
                <p className="text-sm text-muted-foreground italic">Wird geladen…</p>
              ) : upcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Keine geplanten Einsätze.</p>
              ) : (
                <ul className="space-y-1 text-sm" data-testid="vehicle-detail-upcoming">
                  {upcoming.map((t) => (
                    <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-b border-border/40 last:border-0 py-1">
                      <span className="font-medium tabular-nums">{fmtDate(t.trip_date)}</span>
                      <span className="inline-flex items-center gap-1 text-muted-foreground tabular-nums">
                        <Clock className="h-3 w-3" />
                        {t.start_time || "—"}{t.end_time ? `–${t.end_time}` : ""}
                      </span>
                      {t.driver_name && <span className="text-foreground/80">{t.driver_name}</span>}
                      <span className="text-[11px] text-muted-foreground capitalize">· {t.status || "geplant"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
